import Error from "./utils/Error.js";
import global from "./global.js";
const d3 = global.d3;
const log = global.log.getLogger("f_mtac.Tool");

// theme
let Tool = function ({name, data, text, icon, classes, style, position, value, id, enable, disable, handler, draggable, group}) {
    let tool_group = null;
    let enabled = false;
    let draggable_class = "";
    let marker = null;
    Object.defineProperty(this, "group", {
        set: () => {},
        get: () => tool_group
    });
    if (group !== undefined) {
        tool_group = group;
    }

    if (classes === undefined) {
        classes = "";
    }
    if (draggable) {
        draggable_class = "tool-draggable";
    }
    const $button = $("<button/>").attr({
        type: "button",
        class: `btn tool ${draggable_class} ${classes}`,
        "data-tool-name": name
    });
    const $container = $(position);
    if (id !== undefined) {
        $button.attr("id", id);
    }

    if (icon !== undefined) {
        if (typeof icon === "string") {
            $button.append($("<i/>").attr({class: `f_mtac-icon-${icon}`}));
            marker = $("<i/>").attr({class: "f_mtac-icon-resource_plus notification-marker"}).appendTo($button);
        } else if (typeof icon === "object" && icon.constructor.name === "SVGGElement") {
            let svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
            $(svg).attr({width: "100%", viewBox: "10 15 85 70"}).append(icon.cloneNode(true)).appendTo($button);
        }
    }
    if (value !== undefined) {
        $button.attr("value", value);
    }
    if (text !== undefined) {
        $button.append("<span>" + text + "</span>");
    }
    if (style !== undefined) {
        $button.css(style);
    }

    Object.defineProperty(this, "$button", {
        set: () => {},
        get: () => $button
    });
    Object.defineProperty(this, "name", {
        set: () => {},
        get: () => name
    });

    Object.defineProperty(this, "is_draggable", {
        set: () => {},
        get: () => draggable !== undefined
    });

    this.show_marker = function () {
        marker.css("display", null);
    };
    this.hide_marker = function () {
        marker.hide();
    };
    this.get_position = function () {
        return $button[0].getBoundingClientRect();
    };
    this.get_container_position = function () {
        return $container[0].getBoundingClientRect();
    };
    this.is_inside = function (element) {
        let element_position = element.getBoundingClientRect();
        let tool_position = this.get_position();
        let is_inside = 1;
        is_inside &= element_position.top + element_position.height / 2 >= tool_position.top;
        is_inside &= element_position.bottom - element_position.height / 2 <= tool_position.bottom;
        is_inside &= element_position.left + element_position.width / 2 >= tool_position.left;
        is_inside &= element_position.right - element_position.width / 2 <= tool_position.right;
        return is_inside === 1;
    };
    this.button_select = () => {
        $button.removeClass("tool-disabled");
        $button.addClass("tool-enabled");
        $button.addClass("btn-selected");
    };
    this.button_unselect = () => {
        $button.removeClass("tool-enabled");
        $button.addClass("tool-disabled");
        $button.removeClass("btn-selected");
    };
    $button.data("tool", this);
    let blur_timer = null;
    this.enable_click = () => {
        $button.on(`click.tool.${name}`, () => {
            if (blur_timer === null) {
                blur_timer = window.setTimeout(() => {
                    $button.trigger("blur");
                    blur_timer = null;
                }, 100);
            }
            if (enable !== undefined && disable !== undefined) {
                if (enabled && tool_group === null) {
                    enabled = false;
                    disable(this);
                    this.button_unselect();
                } else {
                    enabled = true;
                    enable(this);
                    this.button_select();
                }
            }
            if (handler !== undefined) {
                handler(this);
            }
        });
    };
    this.disable_click = () => {
        $button.off(`click.tool.${name}`);
    };
    this.enable_click();

    this.data = data;
    $container.append($button);
    this.set_handler = (new_handler) => {
        handler = new_handler;
    };
    this.unset_handler = () => {
        handler = undefined;
    };
    this.select = () => {
        if (enable !== undefined && disable !== undefined) {
            if (!enabled) {
                enabled = true;
                enable(this);
                this.button_select();
            }
        }
    };
    this.unselect = () => {
        if (enable !== undefined && disable !== undefined) {
            if (enabled) {
                enabled = false;
                disable(this);
                this.button_unselect();
            }
        }
    };
    var drag;
    this.enable_drag = () => {
        drag = d3.drag();
        drag.on("start", () => {
            this.button_select();
            draggable.start(data, this);
        });
        drag.on("drag", () => {
            draggable.drag(data, this);
        });
        drag.on("end", () => {
            this.disable_drag();
            this.disable_click();
            draggable.end(data, this).then(() => {
                this.button_unselect();
                this.enable_drag();
                this.enable_click();
            });
        });
        drag(d3.select($button[0]));
    };

    this.disable_drag = () => {
        d3.select($button[0]).on(".drag", null);
    };

    if (draggable !== undefined) {
        this.enable_drag();
    }

    this.enable = () => {
        $button.prop("disabled", false);
        if (draggable !== undefined) {
            this.enable_drag();
        }
    };
    this.disable = () => {
        $button.prop("disabled", true);
        if (draggable !== undefined) {
            this.disable_drag();
        }
    };
};

let ToolGroup = function (view, {name, cursor, target, enable, disable}) {
    let tools = {};
    let selected = null;
    let $target = null;
    Object.defineProperty(this, "name", {
        set: () => {},
        get: () => name
    });
    Object.defineProperty(this, "tools", {
        set: () => {},
        get: () => tools
    });
    Object.defineProperty(this, "selected", {
        set: () => {},
        get: () => selected
    });
    switch (target) {
        case "$none":
            $target = null;
            break;
        case "$view":
            $target = view.$container;
            break;
        default:
            $target = view.$container.find(target);
            break;
    }
    let target_enable = () => {
        if ($target === null) {
            return;
        }
        if (cursor === undefined) {
            return;
        }
        $target.on("mousedown.tool_enable", null, () => {
            if (selected !== null) {
                $target.css("cursor", cursor.active);
            } else {
                $target.css("cursor", "default");
            }
        });
        $target.on("mouseup.tool_disable", null, () => {
            if (selected !== null) {
                $target.css("cursor", cursor.idle);
            } else {
                $target.css("cursor", "default");
            }
        });
        $target.css("cursor", cursor.idle);
    };
    let target_disable = () => {
        if ($target === null) {
            return;
        }
        if (cursor === undefined) {
            return;
        }
        $target.off("mousedown.tool_enable");
        $target.off("mouseup.tool_disable");
        $target.css("cursor", "default");
    };
    let group_enable = (tool) => {
        target_enable();
        selected = tool;
        selected.button_select();
        log.info(`tool ${selected.name} enabled`);
        enable($target, selected);
    };
    let group_disable = () => {
        if (selected !== null) {
            log.info(`tool ${selected.name} disabled`);
            target_disable();
            selected.button_unselect();
            disable($target, selected);
            selected = null;
        }
    };
    let handler = (new_tool) => {
        let old_tool = selected;
        if (old_tool !== null) {
            group_disable();
        }
        if (new_tool !== old_tool || (new_tool && new_tool.group !== null)) {
            group_enable(new_tool);
        }
    };
    this.append = (options) => {
        let tool = new Tool(options);
        if (options.name in tools) {
            throw Error.build.exist(`redefining tool ${options.name}`);
        }
        tools[options.name] = tool;
        tool.set_handler(handler);
        return tool;
    };
    this.remove = (tool) => {
        if (!(name in tools)) {
            throw Error.build.noent(`tool ${name} not found`);
        }
        tool.unset_handler();
        delete tools[tool.name];
    };
    this.select = (name) => {
        if (!(name in tools)) {
            throw Error.build.noent(`tool ${name} not found`);
        }
        group_disable();
        group_enable(tools[name]);
    };
    this.unselect = (name) => {
        if (selected === null) {
            return;
        }
        if (name !== undefined && name !== selected.name) {
            return;
        }
        group_disable();
    };
    this.disable = (tool_name) => {
        if (tool_name !== undefined) {
            if (!(tool_name in tools)) {
                throw Error.build.noent(`tool ${name} not found`);
            }
            if (selected !== null && selected.name === tool_name) {
                group_disable();
            }
            return tools[tool_name].disable();
        }
        group_disable();
        for (tool_name in tools) {
            tools[tool_name].disable();
        }
    };
    this.enable = (tool_name) => {
        if (tool_name !== undefined) {
            return tools[tool_name].enable();
        }
        for (tool_name in tools) {
            tools[tool_name].enable();
        }
    };
};

let Tools = function (view) {
    let groups = {},
        tools = {};

    Object.defineProperty(this, "groups", {
        set: () => {},
        get: () => groups
    });
    Object.defineProperty(this, "tools", {
        set: () => {},
        get: () => tools
    });

    this.register_group = (options) => {
        let group = new ToolGroup(view, options);
        if (options.name in groups) {
            throw Error.build.exist(`redefining group ${options.name}`);
        }
        groups[options.name] = group;
        return group;
    };
    this.register = (options) => {
        if ("group" in options) {
            if (!(options.group in groups)) {
                throw Error.build.noent(`group ${options.group} doesn't exist`);
            }
            return groups[options.group].append(options);
        }
        if (options.name in tools) {
            throw Error.build.exist(`redefining tool ${options.name}`);
        }
        let tool = new Tool(options);
        tools[options.name] = tool;
        return tool;
    };
    this.tool_select = (tool_name) => {
        if (tools[tool_name] !== undefined) {
            tools[tool_name].select();
        }
    };
    this.tool_unselect = (tool_name) => {
        if (tool_name === undefined) {
            for (tool_name in tools) {
                tools[tool_name].unselect();
            }
            return;
        }
        tools[tool_name].unselect();
    };
    this.group_select = (group_name, tool_name) => {
        if (groups[group_name] !== undefined) {
            groups[group_name].select(tool_name);
        }
    };
    this.group_unselect = (group_name, tool_name) => {
        if (group_name === undefined) {
            for (group_name in groups) {
                groups[group_name].unselect();
            }
            return;
        }
        groups[group_name].unselect(tool_name);
    };
    this.enable = (group_name, tool_name) => {
        return groups[group_name].enable(tool_name);
    };
    this.disable = (group_name, tool_name) => {
        groups[group_name].disable(tool_name);
    };
};

global.app.bootstrap.register_module({
    name: "tools",
    deps: ["view"],
    init: () => {
        global.tools = new Tools(global.view);
    }
});

export default Tools;
